import React from 'react';
import { CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { StreamStatus, PlayoutServerStatus } from './types';

// Helper function to format duration in seconds to HH:MM:SS
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// Helper function to parse HH:MM:SS to seconds
export function parseDurationToSeconds(duration: string): number {
  const parts = duration.split(':').map(Number);
  return parts[0] * 3600 + parts[1] * 60 + parts[2];
}

// Helper function to calculate total duration from videos
export function calculateTotalDuration(videos: any[]): number {
  return videos.reduce((total, video) => total + (video.duration || 0), 0);
}

// Helper function to format date for display
export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Helper function to check if date is in the future
export function isFutureDate(date: string | Date | any): boolean {
  // Handle dayjs objects from Ant Design DatePicker
  if (date && typeof date.isAfter === 'function') {
    return date.isAfter(new Date());
  }
  // Handle native Date objects and strings
  return new Date(date) > new Date();
}

// Helper function to get status color for stream status
export function getStatusColor(status: StreamStatus): string {
  switch (status) {
    case 'draft': return 'default';
    case 'configured': return 'blue';
    case 'live': return 'green';
    case 'completed': return 'purple';
    case 'cancelled': return 'red';
    default: return 'default';
  }
}

// Helper function to get status icon for playout server status
export function getPlayoutServerStatusIcon(status: PlayoutServerStatus): React.ReactNode {
  switch (status) {
    case 'online':
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    case 'offline':
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    case 'error':
      return <CloseCircleOutlined style={{ color: '#faad14' }} />;
    default:
      return <QuestionCircleOutlined style={{ color: '#d9d9d9' }} />;
  }
}

// Helper function to get status color for playout server status
export function getPlayoutServerStatusColor(status: PlayoutServerStatus): string {
  switch (status) {
    case 'online': return 'success';
    case 'offline': return 'default';
    case 'error': return 'error';
    default: return 'default';
  }
}

// Helper function to format relative time (e.g., "3m ago", "1h ago")
export function formatRelativeTime(iso: string | undefined): string {
  if (!iso) return '-';
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '-';
  const diffMs = now - then;
  if (diffMs < 0) return 'just now';
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Helper function to format uptime in seconds to human-readable (e.g., "2h 34m")
export function formatUptime(seconds: number | undefined): string {
  if (!seconds || seconds <= 0) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// Helper function to get Ant Badge status from monitoring_status
export function getMonitoringBadgeStatus(status: string | undefined): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'healthy': return 'success';
    case 'warning': return 'warning';
    case 'critical': return 'error';
    default: return 'default';
  }
}

// Helper function to check if monitoring data is stale (> 10 minutes old)
export function isMonitoringStale(lastMonitoredAt: string | undefined): boolean {
  if (!lastMonitoredAt) return false;
  const then = new Date(lastMonitoredAt).getTime();
  if (isNaN(then)) return false;
  return Date.now() - then > 10 * 60 * 1000;
}

// Helper function to validate stream data
export function validateStreamData(data: {
  name: string;
  description?: string;
  scheduled_start_time?: string;
  privacy_status?: string;
}): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!data.name || data.name.trim().length === 0) {
    errors.push('Stream name is required');
  }

  if (data.name && data.name.length > 100) {
    errors.push('Stream name must be less than 100 characters');
  }

  if (data.description && data.description.length > 5000) {
    errors.push('Description must be less than 5000 characters');
  }

  if (data.scheduled_start_time) {
    const startDate = new Date(data.scheduled_start_time);
    if (startDate <= new Date()) {
      errors.push('Scheduled start time must be in the future');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}
